import React, { useState, useEffect } from 'react';
import {
  Box,
  Typography,
  Grid,
  Card,
  CardContent,
  Button,
  Chip,
  CircularProgress,
  Alert,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Divider,
  List,
  ListItem,
  ListItemText,
  Paper,
  Stack,
} from '@mui/material';
import {
  AccessTime,
  Assignment,
  CheckCircle,
  Schedule,
  HourglassEmpty,
  Info,
  MenuBook,
  PlayArrow,
  ArrowForward,
} from '@mui/icons-material';
import { useNavigate } from 'react-router-dom';
import { toast } from 'react-toastify';
import { studentExamFlowAPI } from '../../../services/api';

const ExamDashboard = () => {
  const navigate = useNavigate();
  const [loading, setLoading] = useState(true);
  const [examinations, setExaminations] = useState([]);
  const [currentTime, setCurrentTime] = useState(new Date());
  const [selectedExam, setSelectedExam] = useState(null);
  const [selectedSubject, setSelectedSubject] = useState(null);
  const [instructionsDialog, setInstructionsDialog] = useState(false);

  useEffect(() => {
    fetchMyExams();

    // Update current time every second
    const timer = setInterval(() => {
      setCurrentTime(new Date());
    }, 1000);

    return () => clearInterval(timer);
  }, []);

  const fetchMyExams = async () => {
    try {
      setLoading(true);
      const response = await studentExamFlowAPI.getMyExams();
      setExaminations(response.data.data?.examinations || []);
    } catch (error) {
      console.error('Error fetching examinations:', error);
      toast.error('Failed to load examinations');
    } finally {
      setLoading(false);
    }
  };

  const getExamStatus = (exam) => {
    const now = currentTime;
    const startDate = new Date(exam.examination.startDate);
    const endDate = new Date(exam.examination.endDate);

    if (exam.submissionStatus === 'submitted' || exam.submissionStatus === 'auto-submitted') {
      return { status: 'completed', label: 'Completed', color: 'success' };
    }

    if (exam.submissionStatus === 'in-progress') {
      return { status: 'in-progress', label: 'In Progress', color: 'warning' };
    }

    if (now < startDate) {
      return { status: 'upcoming', label: 'Upcoming', color: 'info' };
    }

    if (now >= startDate && now <= endDate) {
      return { status: 'active', label: 'Active', color: 'success' };
    }

    return { status: 'expired', label: 'Expired', color: 'error' };
  };

  const getCountdown = (targetDate) => {
    const now = currentTime;
    const diff = new Date(targetDate) - now;

    if (diff <= 0) return 'Started';

    const days = Math.floor(diff / (1000 * 60 * 60 * 24));
    const hours = Math.floor((diff % (1000 * 60 * 60 * 24)) / (1000 * 60 * 60));
    const minutes = Math.floor((diff % (1000 * 60 * 60)) / (1000 * 60));
    const seconds = Math.floor((diff % (1000 * 60)) / 1000);

    if (days > 0) return `${days}d ${hours}h ${minutes}m`;
    if (hours > 0) return `${hours}h ${minutes}m ${seconds}s`;
    return `${minutes}m ${seconds}s`;
  };

  const handleSubjectClick = (exam, subject) => {
    const examStatus = getExamStatus(exam);
    const subjectStatus = exam.subjectStatuses?.[subject.subjectId];
    const isCompleted = subjectStatus?.status === 'submitted' || subjectStatus?.status === 'auto-submitted';

    if (examStatus.status === 'upcoming') {
      toast.warning('Examination has not started yet');
      return;
    }

    if (examStatus.status === 'expired') {
      toast.error('Examination has ended');
      return;
    }

    if (isCompleted) {
      toast.info('You have already completed this subject');
      return;
    }

    setSelectedExam(exam);
    setSelectedSubject(subject);
    setInstructionsDialog(true);
  };

  const handleStartExam = () => {
    setInstructionsDialog(false);
    navigate(`/student/exam/${selectedExam.examination._id}`, {
      state: {
        studentExamId: selectedExam._id,
        subjectId: selectedSubject.subjectId,
        subjectName: selectedSubject.name
      }
    });
  };

  if (loading) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', minHeight: '60vh' }}>
        <CircularProgress />
      </Box>
    );
  }

  return (
    <Box>
      <Box sx={{ mb: 4 }}>
        <Typography variant="h4" sx={{ fontWeight: 700, mb: 1 }}>
          My Examinations
        </Typography>
        <Typography variant="body2" color="text.secondary">
          View and attend your approved examinations
        </Typography>
      </Box>

      {examinations.length === 0 ? (
        <Alert severity="info" icon={<Info />}>
          No examinations available. Please check back later.
        </Alert>
      ) : (
        <Grid container spacing={3}>
          {examinations.map((exam) => {
            const examStatus = getExamStatus(exam);
            const isActive = examStatus.status === 'active' || examStatus.status === 'in-progress';
            const startDate = new Date(exam.examination.startDate);
            const endDate = new Date(exam.examination.endDate);

            return (
              <Grid item xs={12} key={exam._id}>
                <Card
                  elevation={3}
                  sx={{
                    border: isActive ? '2px solid' : 'none',
                    borderColor: isActive ? 'success.main' : 'transparent',
                  }}
                >
                  <CardContent>
                    {/* Exam Header */}
                    <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'start', mb: 3 }}>
                      <Box>
                        <Typography variant="h5" sx={{ fontWeight: 700, mb: 1 }}>
                          {exam.examination.name}
                        </Typography>
                        <Typography variant="body2" color="text.secondary">
                          {exam.examination.code}
                        </Typography>
                      </Box>
                      <Chip
                        label={examStatus.label}
                        color={examStatus.color}
                        icon={
                          examStatus.status === 'completed' ? <CheckCircle /> :
                          examStatus.status === 'active' ? <Schedule /> :
                          examStatus.status === 'upcoming' ? <HourglassEmpty /> :
                          <AccessTime />
                        }
                      />
                    </Box>

                    {/* Exam Details */}
                    <Grid container spacing={3} sx={{ mb: 3 }}>
                      <Grid item xs={12} sm={6} md={3}>
                        <Typography variant="caption" color="text.secondary">
                          Hall Ticket Number
                        </Typography>
                        <Typography variant="body1" sx={{ fontWeight: 600, color: 'primary.main' }}>
                          {exam.hallTicketNumber}
                        </Typography>
                      </Grid>
                      <Grid item xs={12} sm={6} md={3}>
                        <Typography variant="caption" color="text.secondary">
                          Roll Number
                        </Typography>
                        <Typography variant="body1" sx={{ fontWeight: 600 }}>
                          {exam.rollNumber}
                        </Typography>
                      </Grid>
                      <Grid item xs={12} sm={6} md={3}>
                        <Typography variant="caption" color="text.secondary">
                          Start Date
                        </Typography>
                        <Typography variant="body2">
                          {startDate.toLocaleDateString('en-IN', {
                            year: 'numeric', month: 'short', day: 'numeric',
                            hour: '2-digit', minute: '2-digit'
                          })}
                        </Typography>
                      </Grid>
                      <Grid item xs={12} sm={6} md={3}>
                        <Typography variant="caption" color="text.secondary">
                          End Date
                        </Typography>
                        <Typography variant="body2">
                          {endDate.toLocaleDateString('en-IN', {
                            year: 'numeric', month: 'short', day: 'numeric',
                            hour: '2-digit', minute: '2-digit'
                          })}
                        </Typography>
                      </Grid>
                    </Grid>

                    {examStatus.status === 'upcoming' && (
                      <Box
                        sx={{
                          mb: 3,
                          p: 2,
                          backgroundColor: 'info.lighter',
                          borderRadius: 1,
                          textAlign: 'center'
                        }}
                      >
                        <Typography variant="caption" color="info.dark" display="block">
                          Starts in
                        </Typography>
                        <Typography variant="h6" color="info.main" sx={{ fontWeight: 700 }}>
                          {getCountdown(exam.examination.startDate)}
                        </Typography>
                      </Box>
                    )}

                    {examStatus.status === 'completed' && exam.submittedAt && (
                      <Alert severity="success" sx={{ mb: 3 }}>
                        <Typography variant="body2">
                          <strong>All Subjects Completed!</strong><br />
                          <strong>Submitted At:</strong> {new Date(exam.submittedAt).toLocaleDateString('en-IN', {
                            year: 'numeric', month: 'short', day: 'numeric',
                            hour: '2-digit', minute: '2-digit'
                          })}
                        </Typography>
                      </Alert>
                    )}

                    {examStatus.status === 'in-progress' && (
                      <Alert severity="info" sx={{ mb: 3 }}>
                        <Typography variant="body2">
                          <strong>Progress:</strong> {Object.values(exam.subjectStatuses || {}).filter(s => s.status === 'submitted' || s.status === 'auto-submitted').length} of {exam.examination.subjects.length} subjects completed
                        </Typography>
                      </Alert>
                    )}

                    <Divider sx={{ mb: 3 }} />

                    {/* Subjects */}
                    <Typography variant="h6" sx={{ fontWeight: 600, mb: 2 }}>
                      Subjects
                    </Typography>

                    {exam.examination.subjects && exam.examination.subjects.length > 0 ? (
                      <Grid container spacing={2}>
                        {exam.examination.subjects.map((subject, index) => {
                          const subjectStatus = exam.subjectStatuses?.[subject.subjectId];
                          const isCompleted = subjectStatus?.status === 'submitted' || subjectStatus?.status === 'auto-submitted';
                          const isInProgress = subjectStatus?.status === 'in-progress';
                          const examExpired = examStatus.status === 'expired';
                          const examUpcoming = examStatus.status === 'upcoming';
                          const canStart = examStatus.status === 'active' || examStatus.status === 'in-progress';
                          const isDisabled = isCompleted || examExpired || examUpcoming;

                          return (
                            <Grid item xs={12} sm={6} md={4} key={subject.subjectId || index}>
                              <Paper
                                elevation={2}
                                sx={{
                                  p: 2,
                                  cursor: isDisabled ? 'not-allowed' : (canStart ? 'pointer' : 'default'),
                                  transition: 'all 0.2s',
                                  opacity: isDisabled ? 0.6 : 1,
                                  backgroundColor: isCompleted ? 'success.lighter' : (isInProgress ? 'warning.lighter' : 'background.paper'),
                                  border: isCompleted ? '2px solid' : 'none',
                                  borderColor: isCompleted ? 'success.main' : 'transparent',
                                  '&:hover': !isDisabled && canStart ? {
                                    transform: 'translateY(-4px)',
                                    boxShadow: 4,
                                  } : {},
                                }}
                                onClick={() => !isDisabled && canStart && handleSubjectClick(exam, subject)}
                              >
                                <Stack direction="row" spacing={2} alignItems="center">
                                  <Box
                                    sx={{
                                      width: 48,
                                      height: 48,
                                      borderRadius: '50%',
                                      display: 'flex',
                                      alignItems: 'center',
                                      justifyContent: 'center',
                                      backgroundColor: isCompleted ? 'success.main' : (isInProgress ? 'warning.main' : 'primary.lighter'),
                                      color: isCompleted || isInProgress ? 'white' : 'primary.main',
                                    }}
                                  >
                                    {isCompleted ? <CheckCircle /> : <MenuBook />}
                                  </Box>
                                  <Box sx={{ flexGrow: 1 }}>
                                    <Typography variant="body1" sx={{ fontWeight: 600 }}>
                                      {subject.name}
                                    </Typography>
                                    <Typography variant="caption" color="text.secondary" display="block">
                                      {subject.code}
                                    </Typography>
                                    {isCompleted && (
                                      <Typography variant="caption" color="success.dark" sx={{ fontWeight: 600 }}>
                                        Completed
                                      </Typography>
                                    )}
                                    {isInProgress && (
                                      <Typography variant="caption" color="warning.dark" sx={{ fontWeight: 600 }}>
                                        In Progress
                                      </Typography>
                                    )}
                                    {examUpcoming && (
                                      <Typography variant="caption" color="info.dark">
                                        Not Started
                                      </Typography>
                                    )}
                                    {examExpired && (
                                      <Typography variant="caption" color="error.dark">
                                        Expired
                                      </Typography>
                                    )}
                                  </Box>
                                  {canStart && !isCompleted && (
                                    <ArrowForward color={isInProgress ? 'warning' : 'primary'} />
                                  )}
                                </Stack>
                              </Paper>
                            </Grid>
                          );
                        })}
                      </Grid>
                    ) : (
                      <Alert severity="warning">
                        No subjects available for this examination.
                      </Alert>
                    )}
                  </CardContent>
                </Card>
              </Grid>
            );
          })}
        </Grid>
      )}

      {/* Instructions Dialog */}
      <Dialog
        open={instructionsDialog}
        onClose={() => setInstructionsDialog(false)}
        maxWidth="md"
        fullWidth
      >
        <DialogTitle>
          <Box>
            <Typography variant="h5" sx={{ fontWeight: 700 }}>
              Examination Instructions
            </Typography>
            {selectedSubject && (
              <Typography variant="body2" color="text.secondary" sx={{ mt: 1 }}>
                {selectedSubject.name} ({selectedSubject.code})
              </Typography>
            )}
          </Box>
        </DialogTitle>
        <DialogContent dividers>
          {selectedExam?.examination?.instructions ? (
            <Box sx={{ mb: 3 }}>
              <Typography variant="h6" gutterBottom sx={{ fontWeight: 600, color: 'primary.main' }}>
                Specific Instructions for this Examination
              </Typography>
              <Typography
                variant="body1"
                sx={{ whiteSpace: 'pre-wrap', mb: 3 }}
                dangerouslySetInnerHTML={{ __html: selectedExam.examination.instructions }}
              />
              <Divider sx={{ my: 2 }} />
            </Box>
          ) : null}

          <Box>
            <Typography variant="h6" gutterBottom sx={{ fontWeight: 600, color: 'primary.main' }}>
              General Instructions
            </Typography>

            <Typography variant="subtitle2" sx={{ mt: 2, mb: 1, fontWeight: 600 }}>
              1. Examination Details
            </Typography>
            <Typography variant="body2" sx={{ mb: 2, pl: 2 }}>
              • This is a computer-based online examination<br />
              • The examination contains Multiple Choice Questions (MCQs)<br />
              • Each question has four options, only one option is correct<br />
              • The medium of examination is as per the course selection
            </Typography>

            <Typography variant="subtitle2" sx={{ mt: 2, mb: 1, fontWeight: 600 }}>
              2. Navigation
            </Typography>
            <Typography variant="body2" sx={{ mb: 2, pl: 2 }}>
              • You can navigate between questions using the question palette on the right side<br />
              • Use "Previous" and "Next" buttons to move between questions sequentially<br />
              • Click on any question number to jump directly to that question<br />
              • The current question will be highlighted in the question palette
            </Typography>

            <Typography variant="subtitle2" sx={{ mt: 2, mb: 1, fontWeight: 600 }}>
              3. Answering Questions
            </Typography>
            <Typography variant="body2" sx={{ mb: 2, pl: 2 }}>
              • To select an answer, click on the radio button next to your chosen option<br />
              • To change your answer, simply click on a different option<br />
              • Your answer will be automatically saved when you navigate to another question<br />
              • You can change your answer multiple times before final submission
            </Typography>

            <Typography variant="subtitle2" sx={{ mt: 2, mb: 1, fontWeight: 600 }}>
              4. Question Status Indicators
            </Typography>
            <Typography variant="body2" sx={{ mb: 2, pl: 2 }}>
              • <strong style={{ color: '#2e7d32' }}>Answered:</strong> Questions you have answered (Green)<br />
              • <strong style={{ color: '#ed6c02' }}>Marked for Review:</strong> Questions marked for later review (Orange)<br />
              • <strong style={{ color: '#9e9e9e' }}>Not Answered:</strong> Questions you haven't attempted yet (Gray)<br />
              • <strong style={{ color: '#1976d2' }}>Current:</strong> Question currently being viewed (Blue border)
            </Typography>

            <Typography variant="subtitle2" sx={{ mt: 2, mb: 1, fontWeight: 600 }}>
              5. Mark for Review Feature
            </Typography>
            <Typography variant="body2" sx={{ mb: 2, pl: 2 }}>
              • You can mark any question for review by checking the "Mark for Review" checkbox<br />
              • This allows you to quickly identify questions you want to revisit<br />
              • Marked questions will be highlighted in orange in the question palette<br />
              • You can review these questions before final submission
            </Typography>

            <Typography variant="subtitle2" sx={{ mt: 2, mb: 1, fontWeight: 600 }}>
              6. Auto-Save Functionality
            </Typography>
            <Typography variant="body2" sx={{ mb: 2, pl: 2 }}>
              • Your answers are automatically saved every 30 seconds<br />
              • Answers are also saved when you navigate to another question<br />
              • You will see a "Saving..." indicator when auto-save is in progress<br />
              • This ensures your progress is not lost due to connectivity issues
            </Typography>

            <Typography variant="subtitle2" sx={{ mt: 2, mb: 1, fontWeight: 600 }}>
              7. Time Management
            </Typography>
            <Typography variant="body2" sx={{ mb: 2, pl: 2 }}>
              • The time remaining will be displayed at the top of your screen<br />
              • The timer will turn red when only 5 minutes are remaining<br />
              • Plan your time wisely to answer all questions<br />
              • The examination will be auto-submitted when time expires
            </Typography>

            <Typography variant="subtitle2" sx={{ mt: 2, mb: 1, fontWeight: 600 }}>
              8. Submission Process
            </Typography>
            <Typography variant="body2" sx={{ mb: 2, pl: 2 }}>
              • Review all your answers before submitting<br />
              • Click on the "Submit Exam" button when you are ready<br />
              • You will be asked to confirm your submission<br />
              • Once submitted, you cannot change your answers<br />
              • Ensure you submit before the examination end time
            </Typography>

            <Typography variant="subtitle2" sx={{ mt: 2, mb: 1, fontWeight: 600 }}>
              9. Important Points to Remember
            </Typography>
            <Typography variant="body2" sx={{ mb: 2, pl: 2 }}>
              • Do not close the browser or press the back button during examination<br />
              • Do not refresh the page as it may cause loss of unsaved data<br />
              • Ensure stable internet connection throughout the examination<br />
              • Do not use multiple tabs or windows during the examination<br />
              • Do not attempt to copy questions or use unfair means<br />
              • Any malpractice will result in cancellation of your examination
            </Typography>

            <Typography variant="subtitle2" sx={{ mt: 2, mb: 1, fontWeight: 600 }}>
              10. Technical Requirements
            </Typography>
            <Typography variant="body2" sx={{ mb: 2, pl: 2 }}>
              • Use a desktop/laptop for better experience (mobile is supported)<br />
              • Use latest version of Chrome, Firefox, Safari, or Edge browser<br />
              • Ensure JavaScript is enabled in your browser<br />
              • Minimum screen resolution of 1024x768 recommended<br />
              • Stable broadband internet connection (minimum 2 Mbps)
            </Typography>

            <Typography variant="subtitle2" sx={{ mt: 2, mb: 1, fontWeight: 600 }}>
              11. In Case of Technical Issues
            </Typography>
            <Typography variant="body2" sx={{ mb: 2, pl: 2 }}>
              • If you face any technical difficulties, contact the examination center immediately<br />
              • Do not panic if your internet disconnects briefly - your answers are auto-saved<br />
              • If the page becomes unresponsive, try refreshing once and continue<br />
              • Report any issues to the administrator as soon as possible
            </Typography>
          </Box>

          <Alert severity="error" sx={{ mt: 3, mb: 2 }}>
            <Typography variant="body2" sx={{ fontWeight: 600 }}>
              ⚠️ IMPORTANT WARNING
            </Typography>
            <Typography variant="body2" sx={{ mt: 1 }}>
              • Once you click "I Accept, Start Exam", the examination will begin immediately<br />
              • You cannot pause the examination once started<br />
              • The exam will auto-submit when time expires<br />
              • Ensure you have sufficient time and are ready before starting
            </Typography>
          </Alert>

          <Alert severity="info" sx={{ mb: 2 }}>
            <Typography variant="body2">
              By clicking "I Accept, Start Exam", you acknowledge that you have read and understood all the instructions above.
            </Typography>
          </Alert>
        </DialogContent>
        <DialogActions sx={{ p: 2 }}>
          <Button onClick={() => setInstructionsDialog(false)} variant="outlined">
            Cancel
          </Button>
          <Button
            onClick={handleStartExam}
            variant="contained"
            startIcon={<PlayArrow />}
            size="large"
          >
            I Accept, Start Exam
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default ExamDashboard;
